import React, { useEffect, useMemo, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { MODAL_KEYS } from '_constants/modals';
import { theme } from '_constants/theme';
import { selectPaymentMethodsData, selectPaymentMethodsLoading } from '_store/billing/selector';
import { getPaymentMethods } from '_store/billing/slice';
import { showModal } from '_store/modals/slice';
import { selectCustomerId } from '_store/user/selector';

import Button from 'components/shared/Button';
import Card from 'components/shared/layouts/Card';
import Text from 'components/shared/layouts/Text';

import PaymentMethodsList from './PaymentMethodsList';
import PaymentMethodsTable from './PaymentMethodsTable';

const StyledContainer = styled.div`
  padding-top: ${theme.spaces.large}px;

  @media ${theme.devices.tablet} {
    padding-top: ${theme.spaces.regular}px;
    padding-bottom: ${theme.spaces.regular}px;
  }
`;

const StyledNavContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spaces.large}px;

  @media ${theme.devices.tablet} {
    flex-direction: column;
    gap: ${theme.spaces.regular}px;
  }
`;

const StyledButtonContainer = styled.div``;

const ROWS_PER_PAGE = 10;

const PaymentMethodsView = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const dispatch = useDispatch();
  const customerId = useSelector(selectCustomerId);
  const loading = useSelector(selectPaymentMethodsLoading);
  const list = useSelector(selectPaymentMethodsData);

  const items = useMemo(() => {
    const start = (currentPage - 1) * ROWS_PER_PAGE;
    const end = start + ROWS_PER_PAGE;
    return list?.slice(start, end);
  }, [currentPage, list]);

  const handlePaymentMethodAdd = () => {
    dispatch(showModal({ key: MODAL_KEYS.paymentMethodAdd }));
  };

  useEffect(() => {
    if (customerId) {
      dispatch(getPaymentMethods({ customerId }));
    }
  }, [customerId]);

  return (
    <StyledContainer>
      <Card>
        <StyledNavContainer>
          <Text size={theme.fontSizes.large2X} weight={700} desktop>
            Payment Methods
          </Text>
          <Text size={theme.fontSizes.large} weight={700} centered mobile>
            Payment Methods
          </Text>
          <StyledButtonContainer>
            <Button color={theme.colors.cyanBlue} onClick={handlePaymentMethodAdd}>
              + Add Payment Method
            </Button>
          </StyledButtonContainer>
        </StyledNavContainer>
        {isMobile && (
          <PaymentMethodsList
            loading={loading}
            items={items}
            pageCount={list?.length / ROWS_PER_PAGE}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
          />
        )}
        {!isMobile && (
          <PaymentMethodsTable
            skeletonRowsNum={ROWS_PER_PAGE}
            loading={loading}
            items={items}
            pageCount={list?.length / ROWS_PER_PAGE}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
          />
        )}
      </Card>
    </StyledContainer>
  );
};

export default PaymentMethodsView;
